/** Admin → AI matte: re-key every Generate template with the key values on screen. */
import { useState } from "react";
import { useServices } from "../app/context.js";
import { ConfirmPopover } from "../ui/ConfirmPopover.js";
import type { GuideKeyOptions } from "../editor/chromaKey.js";
import { rekeyTemplate, type RekeyOutcome } from "../templates/rekeyAll.js";

interface Row { name: string; outcome: RekeyOutcome | "failed" }

const SAID: Record<Row["outcome"], string> = {
  "rekeyed": "Re-keyed.",
  "republished": "Re-keyed and published as a new version.",
  "skipped": "",
  "failed": "Not re-keyed. Open it in the editor and save it.",
};

export function RekeyAll({ keyOptions }: { keyOptions: GuideKeyOptions }) {
  const { client } = useServices();
  const [rows, setRows] = useState<Row[]>([]);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const run = async () => {
    setRows([]);
    // The list does not carry a template's bake, so each candidate's full record is read in
    // `rekeyTemplate`, which skips any that was not made from Generate.
    const templates = (await client.listTemplates()).filter((t) => t.currentVersion?.hasBakedAssets !== false);
    setProgress({ done: 0, total: templates.length });
    // One at a time: each keys a full-size image on this device.
    for (const [i, t] of templates.entries()) {
      let outcome: Row["outcome"];
      try {
        outcome = await rekeyTemplate(client, t.templateId, keyOptions);
      } catch {
        outcome = "failed";
      }
      if (outcome !== "skipped") setRows((r) => [...r, { name: t.name, outcome }]);
      setProgress({ done: i + 1, total: templates.length });
    }
  };

  const running = !!progress && progress.done < progress.total;
  return (
    <div className="setting-row rekey-all" data-testid="rekey-all">
      <div>
        <span className="setting-row__label">Re-key all Generate templates</span>
        <p className="faint">Keys every template made from Generate again from its original print guide image, with the key values above, and saves the new case layer. Other devices take it after Sync.</p>
        {progress && <p className="faint" role="status" data-testid="rekey-progress">{progress.done} of {progress.total} templates checked; {rows.filter((r) => r.outcome !== "failed").length} re-keyed</p>}
        {rows.length > 0 && (
          <ul className="rekey-all__rows" data-testid="rekey-rows">
            {rows.map((r, i) => <li key={i}><strong>{r.name}</strong>: {SAID[r.outcome]}</li>)}
          </ul>
        )}
      </div>
      <div className="setting-row__control">
        <ConfirmPopover message="Re-key every Generate template with these key values? Their saved case layers are replaced." confirmLabel="Re-key all" onConfirm={() => void run()}>
          {(toggle) => (
            <button type="button" className="btn btn--sm" disabled={running} onClick={toggle} data-testid="rekey-all-btn">
              {running ? "Re-keying…" : "Re-key all"}
            </button>
          )}
        </ConfirmPopover>
      </div>
    </div>
  );
}
